import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, Space, Typography, Tooltip, Badge } from 'antd';
import { TeamOutlined, AppstoreOutlined } from '@ant-design/icons';
import { adminFetch, getAdminUser } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';

const { Text } = Typography;

interface Tenant {
  id: number;
  name: string;
  email?: string;
  primary_contact?: string;
  status?: string;
}

interface Business {
  id: number;
  tenant_id: number;
  name: string;
  status?: string;
}

interface Application {
  id: number;
  business_id: number;
  tenant_id: number;
  username: string;
  status?: string;
}

export interface HierarchyFilterValues {
  tenantId: string | null;
  businessId: string | null;
  applicationId: string | null;
}

interface HierarchyFilterProps {
  value: HierarchyFilterValues;
  onChange: (values: HierarchyFilterValues) => void;
  showLabels?: boolean;
  style?: React.CSSProperties;
}

function HierarchyFilter({ value, onChange, showLabels = true, style }: HierarchyFilterProps) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [appsLoading, setAppsLoading] = useState(false);

  const adminUser = useMemo(() => getAdminUser(), []);
  const userRole = adminUser?.role || '';
  const userTenantId = adminUser?.tenant_id || adminUser?.client_id;

  const canViewAllTenants = useMemo(() => {
    const allowedRoles = ['super_admin', 'admin', 'analyst', 'approver'];
    return allowedRoles.includes(userRole);
  }, [userRole]);

  // Load tenants
  const loadTenants = useCallback(async () => {
    if (!canViewAllTenants && userTenantId) {
      setTenantsLoading(false);
      return;
    }
    try {
      setTenantsLoading(true);
      const response = await adminFetch(`${getBackendUrl()}/api/admin/clients?limit=1000`);
      if (response.ok) {
        const data = await response.json();
        setTenants(data.data?.clients || []);
      }
    } catch (err) {
      console.error('Failed to load tenants:', err);
    } finally {
      setTenantsLoading(false);
    }
  }, [canViewAllTenants, userTenantId]);

  // Load businesses for selected tenant
  const loadBusinesses = useCallback(async (tenantId: string) => {
    try {
      setBusinessesLoading(true);
      const response = await adminFetch(
        `${getBackendUrl()}/api/admin/tenants/${tenantId}/businesses`
      );
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data.businesses || []);
      }
    } catch (err) {
      console.error('Failed to load businesses:', err);
    } finally {
      setBusinessesLoading(false);
    }
  }, []);

  // Load applications for selected business
  const loadApplications = useCallback(async (tenantId: string, businessId: string) => {
    try {
      setAppsLoading(true);
      const response = await adminFetch(
        `${getBackendUrl()}/api/admin/tenants/${tenantId}/businesses/${businessId}/applications`
      );
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
      }
    } catch (err) {
      console.error('Failed to load applications:', err);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Auto-select tenant for restricted users
  useEffect(() => {
    if (!canViewAllTenants && userTenantId && value.tenantId !== String(userTenantId)) {
      onChange({ tenantId: String(userTenantId), businessId: null, applicationId: null });
    }
  }, [canViewAllTenants, userTenantId, value.tenantId, onChange]);

  // Load businesses when tenant changes
  useEffect(() => {
    if (value.tenantId) {
      loadBusinesses(value.tenantId);
    } else {
      setBusinesses([]);
    }
  }, [value.tenantId, loadBusinesses]);

  // Load applications when business changes
  useEffect(() => {
    if (value.tenantId && value.businessId) {
      loadApplications(value.tenantId, value.businessId);
    } else {
      setApplications([]);
    }
  }, [value.tenantId, value.businessId, loadApplications]);

  const handleTenantChange = (tenantId: string) => {
    onChange({ tenantId: tenantId || null, businessId: null, applicationId: null });
  };

  const handleBusinessChange = (businessId: string) => {
    onChange({ ...value, businessId: businessId || null, applicationId: null });
  };

  const handleApplicationChange = (appId: string) => {
    onChange({ ...value, applicationId: appId || null });
  };

  const tenantOptions = [
    { value: '', label: 'All Tenants' },
    ...tenants.map((t) => ({
      value: String(t.id),
      label: t.name || t.email || `Tenant ${t.id}`,
    })),
  ];

  const statusColor = (status?: string) => {
    const colors: Record<string, string> = {
      active: '#52c41a',
      inactive: '#faad14',
      revoked: '#ff4d4f',
    };
    return colors[status || ''] || '#d9d9d9';
  };

  const statusText = (status?: string) => {
    if (!status || status === 'active') return 'active';
    if (status === 'revoked') return 'deleted';
    return status;
  };

  const renderStatusOption = (option: any) => {
    const s = option.data?.status;
    if (!s) return option.label; // "All ..." options
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: statusColor(s),
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1 }}>{option.data?.name}</span>
        <span
          style={{
            fontSize: 11,
            color: statusColor(s),
            fontWeight: 500,
            textTransform: 'capitalize',
          }}
        >
          {statusText(s)}
        </span>
      </span>
    );
  };

  const businessOptions = [
    { value: '', label: 'All Businesses' },
    ...businesses.map((b) => ({
      value: String(b.id),
      label: b.name || `Business ${b.id}`,
      status: b.status,
      name: b.name || `Business ${b.id}`,
    })),
  ];

  const appOptions = [
    { value: '', label: 'All Applications' },
    ...applications.map((a) => ({
      value: String(a.id),
      label: a.username || `Application ${a.id}`,
      status: a.status,
      name: a.username || `Application ${a.id}`,
    })),
  ];

  // Restricted user: show label instead of dropdown
  if (!canViewAllTenants && userTenantId) {
    const tenantName = adminUser?.tenant_name || adminUser?.name || `Tenant ${userTenantId}`;
    return (
      <Space wrap style={style}>
        {showLabels && (
          <Tooltip title="You can only view data for your own tenant.">
            <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              <TeamOutlined style={{ marginRight: 4 }} />
              Tenant: <Text strong>{tenantName}</Text>
            </Text>
          </Tooltip>
        )}
        <Select
          value={value.businessId || ''}
          onChange={handleBusinessChange}
          style={{ minWidth: 180 }}
          loading={businessesLoading}
          placeholder="Business..."
          options={businessOptions}
          optionRender={renderStatusOption}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 300 }}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
        {value.businessId && (
          <Select
            value={value.applicationId || ''}
            onChange={handleApplicationChange}
            style={{ minWidth: 180 }}
            loading={appsLoading}
            placeholder="Application..."
            options={appOptions}
            optionRender={renderStatusOption}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 300 }}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        )}
      </Space>
    );
  }

  return (
    <Space wrap style={style}>
      {showLabels && (
        <Tooltip title="Filter data by tenant, business, and application hierarchy">
          <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
            <TeamOutlined style={{ marginRight: 4 }} />
            Filter:
          </Text>
        </Tooltip>
      )}
      <Select
        value={value.tenantId || ''}
        onChange={handleTenantChange}
        style={{ minWidth: 180 }}
        loading={tenantsLoading}
        placeholder="Select tenant..."
        options={tenantOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
      {value.tenantId && (
        <Select
          value={value.businessId || ''}
          onChange={handleBusinessChange}
          style={{ minWidth: 180 }}
          loading={businessesLoading}
          placeholder="Business..."
          options={businessOptions}
          optionRender={renderStatusOption}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 300 }}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      )}
      {value.businessId && (
        <Select
          value={value.applicationId || ''}
          onChange={handleApplicationChange}
          style={{ minWidth: 180 }}
          loading={appsLoading}
          placeholder="Application..."
          options={appOptions}
          optionRender={renderStatusOption}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 300 }}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      )}
    </Space>
  );
}

export default HierarchyFilter;
